import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import FrontechLogo from './FrontechLogo';
import Seo from './marketing/Seo';

export default function Terms() {
  return (
    <div style={{ background: 'var(--color-base)', color: 'var(--color-text-primary)', minHeight: '100vh' }}>
      <Seo
        title="Terms of Service · FRONTECH"
        description="Terms governing use of the FRONTECH marketing site and Front SaaS product."
        path="/terms"
        noindex
      />
      <nav className="border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80">
            <FrontechLogo size={22} variant="icon" color="var(--color-accent)" />
            <span className="font-heading text-sm font-semibold">Front</span>
            <span className="text-xs font-mono" style={{ color: 'var(--color-text-muted)' }}>by FRONTECH</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm hover:opacity-80"
            style={{ color: 'var(--color-text-secondary)' }}>
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-5 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-2">Terms of Service</h1>
        <p className="text-xs font-mono uppercase tracking-widest mb-10" style={{ color: 'var(--color-text-muted)' }}>
          Effective date: November 6, 2026
        </p>

        <div className="space-y-8 text-[15px] leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>1. Acceptance</h2>
            <p>
              By creating an account or using Front ("the Service"), you agree to these Terms of Service.
              The Service is provided by FRONTECH Consulting ("FRONTECH", "we", "us"), a company based in Pakistan.
              If you do not agree to these terms, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>2. The Service</h2>
            <p>
              Front provides conversational AI agents (voice, chat, and tool-using) that you can embed into your
              websites, mobile apps, and other digital products. Functionality, plan limits, and pricing are
              described on the public pricing page and in your dashboard.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>3. Your account</h2>
            <p>
              You are responsible for keeping your login credentials secure. You must be at least 18 years old, or
              the age of majority in your jurisdiction, to use the Service. You are responsible for all activity
              that occurs under your account, including agents you create and the end-user conversations they handle.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>4. Acceptable use</h2>
            <p>You agree not to use the Service to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Build agents that impersonate real people without permission.</li>
              <li>Generate or distribute illegal content, spam, malware, or content that violates third-party rights.</li>
              <li>Attempt to reverse-engineer, scrape, or circumvent the Service's rate limits or plan enforcement.</li>
              <li>Use the Service to make decisions about credit, employment, housing, insurance, or other matters with
                  significant legal effects without independent human review.</li>
              <li>Process special-category personal data (health, biometric, etc.) without a lawful basis and a
                  separate data processing agreement with us.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>5. Billing</h2>
            <p>
              Paid plans are billed through Paddle.com Inc. ("Paddle"), our Merchant of Record. Paddle is responsible
              for invoicing, tax collection, and refund processing. Subscriptions renew automatically until cancelled.
              You can cancel at any time from the billing page; cancellation takes effect at the end of the current
              billing period. If you downgrade to a smaller plan, agents above the new plan's limit will be paused
              until you upgrade or pause/delete others. See our{' '}
              <Link to="/refunds" className="underline hover:opacity-80" style={{ color: 'var(--color-accent)' }}>
                Refund Policy
              </Link>{' '}
              for eligibility windows and how to request a refund.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>6. Service availability</h2>
            <p>
              We aim for high availability but do not guarantee uninterrupted service. The Service depends on
              third-party providers (Supabase, OpenAI, Railway, Netlify, Paddle); outages of those providers may
              affect availability. We are not liable for downtime caused by upstream providers.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>7. Intellectual property</h2>
            <p>
              We retain ownership of the Service, the codebase, and all related intellectual property. You retain
              ownership of the content you upload (system prompts, knowledge documents, conversation logs). You
              grant us a limited license to host, process, and transmit your content as needed to provide the Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>8. AI output disclaimer</h2>
            <p>
              The Service uses large language models, which can produce inaccurate or misleading output. You are
              responsible for reviewing agent responses before relying on them in any business-critical context.
              We do not warrant that agent output is accurate, complete, or fit for any particular purpose.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>9. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by law, FRONTECH's total liability for any claim arising from or
              relating to the Service is limited to the amount you paid us in the 12 months preceding the claim.
              We are not liable for indirect, incidental, consequential, or punitive damages.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>10. Termination</h2>
            <p>
              You may close your account at any time by emailing support. We may suspend or terminate accounts that
              violate these terms, with notice where reasonably possible. On termination, your data is deleted per
              our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>11. Changes</h2>
            <p>
              We may update these terms. Material changes will be announced in-product or by email at least
              14 days before they take effect. Continued use after the effective date constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>12. Governing law</h2>
            <p>
              These terms are governed by the laws of the Islamic Republic of Pakistan. Disputes will be resolved
              in the courts of Lahore, Pakistan, unless required otherwise by applicable consumer-protection law.
              Contact: <span style={{ color: 'var(--color-accent)' }}>support@frontech-consulting.com</span>.
            </p>
          </section>
        </div>
      </main>

      <footer className="py-10 px-5 mt-8" style={{ borderTop: '1px solid var(--color-border)' }}>
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            &copy; {new Date().getFullYear()} FRONTECH Consulting. All rights reserved.
          </span>
          <div className="flex items-center gap-5 text-xs">
            <Link to="/privacy" style={{ color: 'var(--color-text-muted)' }} className="hover:opacity-80">Privacy</Link>
            <Link to="/terms" style={{ color: 'var(--color-text-muted)' }} className="hover:opacity-80">Terms</Link>
            <Link to="/refunds" style={{ color: 'var(--color-text-muted)' }} className="hover:opacity-80">Refunds</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
